// Roof_Pure.ts — PURE roof-generation engine for the Buildings Set1 roof layer (§5).
// No three / mobx-graphlink / DOM / MapObject imports — the Node vitest lane imports this
// directly. The DB-dependent "which rooms / rects / wallTopZ per floor" collection lives in
// `BuildingGeometryAccessors.ts`; this file does the rectilinear union, per-floor exposure diff,
// clustering, eave expansion, and the distance-field hip/gable/flat roof surface that are
// independent of the store.
//
// Design (per the plan §5):
//   §5.1 Union via a COORDINATE LATTICE, not a fixed grid: collect the sorted unique X and Y
//        coords of every rect edge; mark the lattice cells each floor covers; exposed(n) =
//        cells(n) \ cells(n+1); 4-connectivity over the exposed cells = roof clusters; each
//        cluster's boundary is traced via `TraceEdgesToPolygon` + `OptimizePolygon`.
//   §5.2 Eave overhang = expand each contributing rect by `eaveOverhang` and RE-UNION on a lattice
//        of the expanded coords — exact square corners, no miter code.
//   §5.3 Constant-pitch hip surface = z = pitch × d(x,y), where d is the Euclidean distance to the
//        polygon boundary (the straight-skeleton crease set, no skeleton required). Gable = d
//        against the eave edges PARALLEL to the ridge axis (a plain gable on a single rect; a
//        gable/step-gable over any rectilinear footprint), with vertical end walls on every
//        perpendicular boundary edge. Flat = z = 0 + parapet ring.
//
// The emitted `MeshPlan`'s groups are tagged with SEMANTIC slots ("roofSurface" / "roofFascia" /
// "roofSoffit") that the renderer maps to recipe ids via the room's resolved materials (the same
// convention the wall/floor/opening layer uses).
import type {Vec2Simple} from "../../Utils/ThreeJS/VectorStructs.js";
import {MeshBuilder, type MeshPlan, type V3} from "./MeshPlan.js";
import {IsPointInPolygon, TraceEdgesToPolygon, OptimizePolygon} from "../../Utils/ThreeJS/PolygonUtils.js";
import {Triangulate2D} from "./Triangulate_Pure.js";

/** Semantic material slots for the roof geometry (the §5 roof consumes three slots). */
export const ROOF_MATERIAL_SLOTS = {
	surface: "roofSurface",
	fascia: "roofFascia",
	soffit: "roofSoffit",
} as const;

/** An axis-aligned rectangle (metres, building-local frame) contributing to the roof union. */
export type RoofRect = {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
};

/** Resolved roof parameters (already merged through the §3.3 chain, no nulls). `style` is a
 *  `RoofStyle` enum value ("flat" | "hip" | "gable"). */
export type RoofParams = {
	style: string;
	/** When `style == gable`, ridge along the LONG axis by default; true flips it to the SHORT axis. */
	gableAlongShorter: boolean;
	pitch: number;            // rise/run
	eaveOverhang: number;
	fasciaDepth: number;
	soffitEnabled: boolean;
	ridgeHeightMax: number;
	latticeStep: number;
};

/** The input the accessor layer hands to the pure engine: per floor number the contributing rects
 *  and the floor's `wallTopZ` (building-local Z where that floor's roof sits), plus the resolved
 *  roof params. */
export type ResolveRoofPlan_Input = {
	rectsByFloor: Map<number, RoofRect[]>;
	wallTopZByFloor: Map<number, number>;
	params: RoofParams;
};

/** One connected roof cluster: the exposed footprint of a floor, its eave-expanded boundary, and
 *  the resolved style applied to it. */
export type RoofCluster = {
	floorNumber: number;
	/** Roof base Z (building-local) = the floor's `wallTopZ`. */
	baseZ: number;
	/** The cluster's boundary WITHOUT eave overhang (the wall-top footprint), CCW + closed. */
	baseBoundary: Vec2Simple[];
	/** The cluster's eave boundary (base expanded by `eaveOverhang`), CCW + closed. */
	eaveBoundary: Vec2Simple[];
	/** AABB of the eave footprint (for gable ridge axis + surface UV origin). */
	bounds: {minX: number, minY: number, maxX: number, maxY: number};
	/** True when the cluster's footprint is a single rectangle (a plain gable with two end walls). */
	isSingleRect: boolean;
	/** The style actually applied to this cluster ("flat" | "hip" | "gable"). */
	style: string;
	warnings: string[];
};

export type ResolveRoofPlan_Result = {
	clusters: RoofCluster[];
	/** The combined roof MeshPlan (surface + fascia + soffit), tagged by roof material slots. */
	mesh: MeshPlan;
	warnings: string[];
};

const EMPTY = new Set<number>();

// ---------------------------------------------------------------------------
// Coordinate lattice (§5.1)
// ---------------------------------------------------------------------------
type Lattice = {
	xs: number[];
	ys: number[];
	xi: Map<number, number>;
	yi: Map<number, number>;
	cols: number; // number of x cells = xs.length - 1
	rows: number; // number of y cells
};

function buildLattice(rects: RoofRect[]): Lattice {
	const xs = new Set<number>(), ys = new Set<number>();
	for (const r of rects) {
		xs.add(r.minX); xs.add(r.maxX);
		ys.add(r.minY); ys.add(r.maxY);
	}
	const xArr = [...xs].sort((a, b)=>a - b);
	const yArr = [...ys].sort((a, b)=>a - b);
	const xi = new Map<number, number>(), yi = new Map<number, number>();
	xArr.forEach((v, i)=>xi.set(v, i));
	yArr.forEach((v, i)=>yi.set(v, i));
	return {xs: xArr, ys: yArr, xi, yi, cols: Math.max(0, xArr.length - 1), rows: Math.max(0, yArr.length - 1)};
}

/** Mark the lattice cells fully covered by `rects` (cell (i,j) spans xs[i]..xs[i+1] × ys[j]..ys[j+1]).
 *  Because the lattice is built FROM the rect edges, every cell is fully in or fully out — a cell is
 *  covered iff all four corner coords are within the rect. */
function markCells(lattice: Lattice, rects: RoofRect[]): Set<number> {
	const s = new Set<number>();
	for (const r of rects) {
		const i0 = lattice.xi.get(r.minX), i1 = lattice.xi.get(r.maxX);
		const j0 = lattice.yi.get(r.minY), j1 = lattice.yi.get(r.maxY);
		if (i0 == null || i1 == null || j0 == null || j1 == null) continue;
		for (let i = i0; i < i1; i++) {
			for (let j = j0; j < j1; j++) s.add(j * lattice.cols + i);
		}
	}
	return s;
}

/** Connected components of a set of lattice cells via 4-connectivity. */
function clusterCells(cells: Set<number>, cols: number, rows: number): Array<Set<number>> {
	const comps: Array<Set<number>> = [];
	const visited = new Set<number>();
	for (const start of cells) {
		if (visited.has(start)) continue;
		const comp = new Set<number>();
		const stack = [start];
		while (stack.length) {
			const idx = stack.pop()!;
			if (visited.has(idx)) continue;
			visited.add(idx);
			comp.add(idx);
			const i = idx % cols, j = Math.floor(idx / cols);
			if (i > 0 && cells.has(idx - 1) && !visited.has(idx - 1)) stack.push(idx - 1);
			if (i < cols - 1 && cells.has(idx + 1) && !visited.has(idx + 1)) stack.push(idx + 1);
			if (j > 0 && cells.has(idx - cols) && !visited.has(idx - cols)) stack.push(idx - cols);
			if (j < rows - 1 && cells.has(idx + cols) && !visited.has(idx + cols)) stack.push(idx + cols);
		}
		comps.push(comp);
	}
	return comps;
}

// ---------------------------------------------------------------------------
// Union (§5.1) + eave expansion (§5.2)
// ---------------------------------------------------------------------------
/** The union of a set of axis-aligned rects, as a CCW + closed boundary polygon. The lattice is
 *  built from the rects' own edge coordinates, so the union is EXACT (no rasterization error). */
function unionRects(rects: RoofRect[]): Vec2Simple[] {
	if (rects.length == 0) return [];
	const lattice = buildLattice(rects);
	if (lattice.cols <= 0 || lattice.rows <= 0) return [];
	const cells = markCells(lattice, rects);
	const {xs, ys, cols, rows} = lattice;

	// Trace the boundary: an edge of a covered cell is a boundary edge when the neighbor cell is
	// uncovered (or out of range). Directions are chosen so a CCW polygon has interior on the left.
	const edges: Array<{start: Vec2Simple, end: Vec2Simple}> = [];
	const cellAt = (i: number, j: number): boolean =>
		i >= 0 && j >= 0 && i < cols && j < rows && cells.has(j * cols + i);
	for (let i = 0; i < cols; i++) {
		for (let j = 0; j < rows; j++) {
			if (!cellAt(i, j)) continue;
			const x0 = xs[i], x1 = xs[i + 1], y0 = ys[j], y1 = ys[j + 1];
			if (!cellAt(i, j - 1)) edges.push({start: [x0, y0], end: [x1, y0]});
			if (!cellAt(i, j + 1)) edges.push({start: [x1, y1], end: [x0, y1]});
			if (!cellAt(i - 1, j)) edges.push({start: [x0, y1], end: [x0, y0]});
			if (!cellAt(i + 1, j)) edges.push({start: [x1, y0], end: [x1, y1]});
		}
	}
	if (edges.length == 0) return [];

	const traced = TraceEdgesToPolygon(edges) as Vec2Simple[];
	const opt = OptimizePolygon(traced);
	if (opt.length < 3) return opt;
	// Close + ensure CCW (the traced boundary is already CCW from the edge directions).
	const closed = opt.map(p=>[p[0], p[1]] as Vec2Simple);
	const [x0, y0] = closed[0], [lx, ly] = closed[closed.length - 1];
	if (Math.abs(lx - x0) > 1e-9 || Math.abs(ly - y0) > 1e-9) closed.push([x0, y0]);
	return closed;
}

function expandRect(r: RoofRect, d: number): RoofRect {
	return {minX: r.minX - d, minY: r.minY - d, maxX: r.maxX + d, maxY: r.maxY + d};
}

function rectAABB(rects: RoofRect[]): {minX: number, minY: number, maxX: number, maxY: number} {
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	for (const r of rects) {
		if (r.minX < minX) minX = r.minX;
		if (r.minY < minY) minY = r.minY;
		if (r.maxX > maxX) maxX = r.maxX;
		if (r.maxY > maxY) maxY = r.maxY;
	}
	return {minX, minY, maxX, maxY};
}

// ---------------------------------------------------------------------------
// Distance field (§5.3)
// ---------------------------------------------------------------------------
function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
	const dx = bx - ax, dy = by - ay;
	const l2 = dx * dx + dy * dy;
	let t = l2 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
	t = Math.max(0, Math.min(1, t));
	return Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
}

/** Distance from `p` to the nearest edge of a CLOSED polygon. */
function distanceToPolygonBoundary(p: [number, number], poly: Vec2Simple[]): number {
	let dmin = Infinity;
	for (let i = 0; i < poly.length - 1; i++) {
		const a = poly[i], c = poly[i + 1];
		const d = distToSegment(p[0], p[1], a[0], a[1], c[0], c[1]);
		if (d < dmin) dmin = d;
	}
	return dmin;
}

/** The ridge axis for a gable: the longer dimension of the cluster's AABB. */
type RidgeAxis = "x" | "y";

/** Gable distance function: d against the eave-boundary edges PARALLEL to the ridge axis.
 *  Produces a ridge running along the axis with vertical gable-end walls on every perpendicular
 *  boundary edge. For a single-rect cluster this reduces to the two long edges (a plain gable). */
function gableDFunc(eaveBoundary: Vec2Simple[], axis: RidgeAxis): (x: number, y: number)=>number {
	const edges: Array<[number, number, number, number]> = [];
	for (let i = 0; i < eaveBoundary.length - 1; i++) {
		const a = eaveBoundary[i], c = eaveBoundary[i + 1];
		if (axis == "x" ? Math.abs(a[1] - c[1]) < 1e-9 : Math.abs(a[0] - c[0]) < 1e-9) {
			edges.push([a[0], a[1], c[0], c[1]]);
		}
	}
	if (edges.length == 0) {
		// Degenerate: no ridge-parallel edges — fall back to distance to the whole boundary (hip-like).
		return (x: number, y: number)=>distanceToPolygonBoundary([x, y], eaveBoundary);
	}
	return (x: number, y: number)=>{
		let dmin = Infinity;
		for (const e of edges) {
			const d = distToSegment(x, y, e[0], e[1], e[2], e[3]);
			if (d < dmin) dmin = d;
		}
		return dmin;
	};
}

/** A surface lattice over a rectilinear polygon, subdivided to `step` but SNAPPED so every
 *  polygon-edge coordinate is a lattice line (so boundary cells are never partially cut). */
function buildSurfaceLattice(poly: Vec2Simple[], step: number): {xs: number[], ys: number[]} {
	const xset = new Set<number>(), yset = new Set<number>();
	for (const p of poly) { xset.add(p[0]); yset.add(p[1]); }
	return {
		xs: subdivideCoord([...xset].sort((a, b)=>a - b), step),
		ys: subdivideCoord([...yset].sort((a, b)=>a - b), step),
	};
}
function subdivideCoord(coords: number[], step: number): number[] {
	const out: number[] = [];
	for (let i = 0; i < coords.length; i++) {
		out.push(coords[i]);
		if (i + 1 < coords.length) {
			let x = coords[i] + step;
			while (x < coords[i + 1] - step * 0.5) { out.push(x); x += step; }
		}
	}
	return out;
}

/** Unit direction of steepest ascent of the distance function at (x, y) (numerical gradient). Used
 *  for the roof surface UV frame (shingle/tile run down-slope, §5.3). */
function slopeDir(dFunc: (x: number, y: number)=>number, x: number, y: number): [number, number] {
	const e = 1e-3;
	const d0 = dFunc(x, y);
	const gx = dFunc(x + e, y) - d0;
	const gy = dFunc(x, y + e) - d0;
	const l = Math.hypot(gx, gy);
	if (l < 1e-9) return [0, 0];
	return [gx / l, gy / l];
}

// ---------------------------------------------------------------------------
// Surface + fascia + soffit geometry
// ---------------------------------------------------------------------------
/** Emit a triangle with its geometric normal (cross product) so the roof surface shades correctly. */
function emitTri(b: MeshBuilder, A: {pos: V3, uv: [number, number]}, B: {pos: V3, uv: [number, number]}, C: {pos: V3, uv: [number, number]}): void {
	const ux = B.pos[0] - A.pos[0], uy = B.pos[1] - A.pos[1], uz = B.pos[2] - A.pos[2];
	const vx = C.pos[0] - A.pos[0], vy = C.pos[1] - A.pos[1], vz = C.pos[2] - A.pos[2];
	let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
	const l = Math.hypot(nx, ny, nz) || 1;
	nx /= l; ny /= l; nz /= l;
	b.tri(A.pos, B.pos, C.pos, [nx, ny, nz], A.uv, B.uv, C.uv);
}

/** The pitched (hip or gable) roof surface as a triangulated heightfield over the cluster's eave
 *  boundary. `dFunc` is the distance function (hip: to the boundary; gable: to the long edges). */
function buildPitchedSurface(b: MeshBuilder, cluster: RoofCluster, params: RoofParams, dFunc: (x: number, y: number)=>number): void {
	const {xs, ys} = buildSurfaceLattice(cluster.eaveBoundary, params.latticeStep);
	const baseZ = cluster.baseZ;
	const poly = cluster.eaveBoundary;
	const originX = cluster.bounds.minX, originY = cluster.bounds.minY;
	const ridgeMax = params.ridgeHeightMax;

	b.setMaterialSlot(ROOF_MATERIAL_SLOTS.surface);
	for (let i = 0; i < xs.length - 1; i++) {
		for (let j = 0; j < ys.length - 1; j++) {
			const cx = (xs[i] + xs[i + 1]) / 2, cy = (ys[j] + ys[j + 1]) / 2;
			if (!IsPointInPolygon(cx, cy, poly)) continue;
			const corners: Array<[number, number]> = [[xs[i], ys[j]], [xs[i + 1], ys[j]], [xs[i + 1], ys[j + 1]], [xs[i], ys[j + 1]]];
			const verts = corners.map(c=>{
				const d = dFunc(c[0], c[1]);
				const z = baseZ + Math.min(params.pitch * d, ridgeMax);
				const [slX, slY] = slopeDir(dFunc, c[0], c[1]);
				const ax = c[0] - originX, ay = c[1] - originY;
				const u = ax * slX + ay * slY;
				const v = -ay * slX + ax * slY;
				return {pos: [c[0], c[1], z] as V3, uv: [u, v] as [number, number]};
			});
			emitTri(b, verts[0], verts[1], verts[2]);
			emitTri(b, verts[0], verts[2], verts[3]);
		}
	}
}

/** Flat roof surface: the eave footprint triangulated at `baseZ` (a parapet rises around it via
 *  `buildFascia`). */
function buildFlatSurface(b: MeshBuilder, cluster: RoofCluster): void {
	const tris = Triangulate2D(cluster.eaveBoundary);
	if (!tris) return;
	const z = cluster.baseZ;
	const poly = cluster.eaveBoundary;
	b.setMaterialSlot(ROOF_MATERIAL_SLOTS.surface);
	for (let i = 0; i < tris.length; i += 3) {
		const A = poly[tris[i]], B = poly[tris[i + 1]], C = poly[tris[i + 2]];
		b.tri([A[0], A[1], z], [B[0], B[1], z], [C[0], C[1], z], [0, 0, 1],
			[A[0], A[1]], [B[0], B[1]], [C[0], C[1]]);
	}
}

/** The fascia skirt around the eave boundary. For hip/gable it DROPS `fasciaDepth` from the eave
 *  (`baseZ`); for flat it is a PARAPET ring RISING `fasciaDepth` above `baseZ`. */
function buildFascia(b: MeshBuilder, cluster: RoofCluster, params: RoofParams): void {
	const poly = cluster.eaveBoundary;
	const baseZ = cluster.baseZ;
	const rising = cluster.style == "flat";
	const zBot = rising ? baseZ : baseZ - params.fasciaDepth;
	const zTop = rising ? baseZ + params.fasciaDepth : baseZ;
	const h = Math.abs(zTop - zBot);
	if (h < 1e-9) return;
	b.setMaterialSlot(ROOF_MATERIAL_SLOTS.fascia);
	for (let i = 0; i < poly.length - 1; i++) {
		const a = poly[i], c = poly[i + 1];
		const dx = c[0] - a[0], dy = c[1] - a[1];
		const len = Math.hypot(dx, dy) || 1;
		const nx = dy / len, ny = -dx / len; // outward (CCW polygon)
		const A: V3 = [a[0], a[1], zBot], B: V3 = [c[0], c[1], zBot];
		const C: V3 = [c[0], c[1], zTop], D: V3 = [a[0], a[1], zTop];
		b.quad(A, B, C, D, [nx, ny, 0], [0, 0], [len, 0], [len, h], [0, h]);
	}
}

/** The soffit: a horizontal quad ring from the fascia bottom inward to the wall face, at
 *  `baseZ - fasciaDepth`. Requires the base and eave boundaries to have matching vertex counts
 *  (they do — the eave is the base offset outward by `eaveOverhang`, preserving vertex count for
 *  rectilinear polygons); otherwise it is skipped with a warning. */
function buildSoffit(b: MeshBuilder, cluster: RoofCluster, params: RoofParams): void {
	const base = cluster.baseBoundary, eave = cluster.eaveBoundary;
	if (base.length != eave.length) {
		cluster.warnings.push(`Soffit skipped on floor ${cluster.floorNumber}: base/eave boundary vertex count mismatch.`);
		return;
	}
	if (base.length < 3) return;
	const z = cluster.baseZ - params.fasciaDepth;
	// Align the eave loop to start at the corner nearest base[0] (both are CCW, so index i then
	// corresponds between the two loops).
	let bi = 0, bd = Infinity;
	for (let i = 0; i < eave.length; i++) {
		const d = (eave[i][0] - base[0][0]) ** 2 + (eave[i][1] - base[0][1]) ** 2;
		if (d < bd) { bd = d; bi = i; }
	}
	const eaveAligned = eave.slice(bi).concat(eave.slice(0, bi));
	b.setMaterialSlot(ROOF_MATERIAL_SLOTS.soffit);
	const N: V3 = [0, 0, -1]; // faces downward
	for (let i = 0; i < base.length; i++) {
		const e0 = eaveAligned[i], e1 = eaveAligned[(i + 1) % base.length];
		const b0 = base[i], b1 = base[(i + 1) % base.length];
		// Order D(=b0), C(=b1), B(=e1), A(=e0) so the normal is -z (see windup analysis).
		const D: V3 = [b0[0], b0[1], z], C: V3 = [b1[0], b1[1], z];
		const B: V3 = [e1[0], e1[1], z], A: V3 = [e0[0], e0[1], z];
		const w = Math.hypot(e1[0] - e0[0], e1[1] - e0[1]);
		const dep = Math.hypot(b0[0] - e0[0], b0[1] - e0[1]);
		b.quad(D, C, B, A, N, [0, 0], [w, 0], [w, dep], [0, dep]);
	}
}

/** Emit a vertical wall triangle with a GIVEN outward normal, correcting the fan winding so the
 *  geometric normal agrees with `N` (robust for arbitrary edge directions, including reflex /
 *  notch gable ends). */
function emitWallTri(b: MeshBuilder, N: V3, A: V3, B: V3, C: V3, uv0: [number, number], uv1: [number, number], uv2: [number, number]): void {
	const ux = B[0] - A[0], uy = B[1] - A[1], uz = B[2] - A[2];
	const vx = C[0] - A[0], vy = C[1] - A[1], vz = C[2] - A[2];
	const cx = uy * vz - uz * vy, cy = uz * vx - ux * vz, cz = ux * vy - uy * vx;
	if (cx * N[0] + cy * N[1] + cz * N[2] < 0) b.tri(A, C, B, N, uv0, uv2, uv1);
	else b.tri(A, B, C, N, uv0, uv1, uv2);
}

/** Gable-end walls: a vertical wall along EVERY eave-boundary edge perpendicular to the ridge
 *  axis (just the two short ends for a single rect; step/notch ends for a rectilinear footprint). */
function buildGableEndWalls(b: MeshBuilder, cluster: RoofCluster, params: RoofParams, dFunc: (x: number, y: number)=>number, axis: RidgeAxis): void {
	const poly = cluster.eaveBoundary;
	for (let i = 0; i < poly.length - 1; i++) {
		const a = poly[i], c = poly[i + 1];
		const perpendicular = axis == "x" ? Math.abs(a[1] - c[1]) > 1e-9 : Math.abs(a[0] - c[0]) > 1e-9;
		if (perpendicular) buildEndWallSegment(b, a, c, cluster, params, dFunc);
	}
}

/** A single vertical gable-end wall segment along the boundary edge `a -> c` (which is
 *  perpendicular to the ridge axis). The wall rises from `baseZ` at both corners to the roof
 *  height (via `dFunc`) at the sampled interior points. */
function buildEndWallSegment(b: MeshBuilder, a: Vec2Simple, c: Vec2Simple, cluster: RoofCluster, params: RoofParams, dFunc: (x: number, y: number)=>number): void {
	const dx = c[0] - a[0], dy = c[1] - a[1];
	const len = Math.hypot(dx, dy);
	if (len < 1e-9) return;
	const baseZ = cluster.baseZ;
	const N: V3 = [dy / len, -dx / len, 0]; // outward normal (eave boundary is CCW)
	const {xs, ys} = buildSurfaceLattice(cluster.eaveBoundary, params.latticeStep);
	const vertical = Math.abs(dx) < 1e-9; // edge varies in y (vertical) vs x (horizontal)
	const coords = vertical ? ys : xs;
	const aCoord = vertical ? a[1] : a[0];
	const cCoord = vertical ? c[1] : c[0];
	const lo = Math.min(aCoord, cCoord);
	const hi = Math.max(aCoord, cCoord);
	// Order the interior samples MONOTONICALLY along the edge direction a -> c (the fan
	// (a, samples, c) must cover the gable profile; always-ascending made left-edge caps
	// self-overlapping + invisible, since the boundary edge there runs top->bottom).
	const interior = coords.filter(v=>v > lo + 1e-9 && v < hi - 1e-9).sort((p, q)=>aCoord <= cCoord ? p - q : q - p);
	const pts: Array<[number, number, number]> = [];
	pts.push([a[0], a[1], baseZ]);
	for (const v of interior) {
		const x = vertical ? a[0] : v;
		const y = vertical ? v : a[1];
		const z = baseZ + Math.min(params.pitch * dFunc(x, y), params.ridgeHeightMax);
		pts.push([x, y, z]);
	}
	pts.push([c[0], c[1], baseZ]);
	if (pts.length < 3) return;
	b.setMaterialSlot(ROOF_MATERIAL_SLOTS.surface);
	for (let k = 1; k < pts.length - 1; k++) {
		const P0 = pts[0], Pk = pts[k], Pk1 = pts[k + 1];
		const uk = vertical ? Math.abs(Pk[1] - a[1]) : Math.abs(Pk[0] - a[0]);
		const uk1 = vertical ? Math.abs(Pk1[1] - a[1]) : Math.abs(Pk1[0] - a[0]);
		const uv0: [number, number] = [0, 0];
		const uvk: [number, number] = [uk, Pk[2] - baseZ];
		const uvk1: [number, number] = [uk1, Pk1[2] - baseZ];
		emitWallTri(b, N, P0 as V3, Pk as V3, Pk1 as V3, uv0, uvk, uvk1);
	}
}

function buildRoofGeometry(b: MeshBuilder, cluster: RoofCluster, params: RoofParams): void {
	const style = cluster.style;
	if (style == "flat") {
		buildFlatSurface(b, cluster);
		buildFascia(b, cluster, params);
	} else if (style == "gable") {
		const w = cluster.bounds.maxX - cluster.bounds.minX;
		const h = cluster.bounds.maxY - cluster.bounds.minY;
		// Default = ridge along the LONG axis; `gableAlongShorter` flips it to the SHORT axis.
		const axis: RidgeAxis = (w >= h) !== params.gableAlongShorter ? "x" : "y";
		const dFunc = gableDFunc(cluster.eaveBoundary, axis);
		buildPitchedSurface(b, cluster, params, dFunc);
		buildGableEndWalls(b, cluster, params, dFunc, axis);
		buildFascia(b, cluster, params);
		if (params.soffitEnabled) buildSoffit(b, cluster, params);
	} else { // hip
		const dFunc = (x: number, y: number)=>distanceToPolygonBoundary([x, y], cluster.eaveBoundary);
		buildPitchedSurface(b, cluster, params, dFunc);
		buildFascia(b, cluster, params);
		if (params.soffitEnabled) buildSoffit(b, cluster, params);
	}
}

// ---------------------------------------------------------------------------
// Top-level engine
// ---------------------------------------------------------------------------
/** Compute the building's roof plan: per-floor exposure diff → clusters → eave-expanded
 *  boundaries → the distance-field surface + fascia + soffit MeshPlan. Pure + node-testable. */
export function ResolveRoofPlan_Pure(input: ResolveRoofPlan_Input): ResolveRoofPlan_Result {
	const warnings: string[] = [];
	const allRects: RoofRect[] = [];
	const floorNumbers: number[] = [];
	for (const [n, rects] of input.rectsByFloor) {
		floorNumbers.push(n);
		for (const r of rects) allRects.push(r);
	}
	floorNumbers.sort((a, b)=>a - b);
	if (allRects.length == 0) {
		return {clusters: [], mesh: {positions: [], normals: [], uvs: [], groups: []}, warnings};
	}

	// Global lattice spanning ALL floors, so exposure diff + clustering share cell coordinates.
	const lattice = buildLattice(allRects);
	const cellsByFloor = new Map<number, Set<number>>();
	for (const n of floorNumbers) cellsByFloor.set(n, markCells(lattice, input.rectsByFloor.get(n) ?? []));

	// Per-floor exposure + clustering.
	const rawClusters: Array<{floorNumber: number, rects: RoofRect[]}> = [];
	for (const n of floorNumbers) {
		const above = cellsByFloor.get(n + 1) ?? EMPTY;
		const exposed = new Set<number>();
		for (const c of cellsByFloor.get(n) ?? []) if (!above.has(c)) exposed.add(c);
		if (exposed.size == 0) continue;
		const floorRects = input.rectsByFloor.get(n) ?? [];
		for (const comp of clusterCells(exposed, lattice.cols, lattice.rows)) {
			const contributing = floorRects.filter(r=>{
				const rc = markCells(lattice, [r]);
				for (const c of rc) if (comp.has(c)) return true;
				return false;
			});
			if (contributing.length == 0) continue;
			rawClusters.push({floorNumber: n, rects: contributing});
		}
	}

	const b = new MeshBuilder();
	const clusters: RoofCluster[] = [];
	for (const rc of rawClusters) {
		const baseZ = input.wallTopZByFloor.get(rc.floorNumber) ?? 0;
		const baseBoundary = unionRects(rc.rects);
		const expanded = rc.rects.map(r=>expandRect(r, input.params.eaveOverhang));
		const eaveBoundary = unionRects(expanded);
		if (baseBoundary.length < 3 || eaveBoundary.length < 3) {
			warnings.push(`Roof cluster on floor ${rc.floorNumber}: degenerate footprint; skipped.`);
			continue;
		}
		const bounds = rectAABB(expanded);
		const style = input.params.style;
		const clusterWarnings: string[] = [];
		const cluster: RoofCluster = {
			floorNumber: rc.floorNumber,
			baseZ,
			baseBoundary,
			eaveBoundary,
			bounds,
			isSingleRect: rc.rects.length == 1,
			style,
			warnings: clusterWarnings,
		};
		clusters.push(cluster);
		warnings.push(...clusterWarnings);
		buildRoofGeometry(b, cluster, input.params);
	}

	return {clusters, mesh: b.build(), warnings};
}
